import React, { useState, useEffect } from 'react';
import { Star, Heart, MapPin } from 'lucide-react';
import { Location } from '../types/chat';
import { getPlacePhotos } from '../services/places';
import { getLocationTypeAndIcon } from '../utils/locationTypeUtils';

interface LocationRecommendationProps {
  location: Location;
  index: number;
  isVisible: boolean;
  onClick: () => void;
  onHoverEnter?: (locationId: string) => void;
  onHoverLeave?: () => void;
  isActive?: boolean;
}

export const LocationRecommendation: React.FC<LocationRecommendationProps> = ({ 
  location,
  index,
  isVisible,
  onClick,
  onHoverEnter,
  onHoverLeave,
  isActive = false
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [locationImage, setLocationImage] = useState<string>('');
  const [isFavorite, setIsFavorite] = useState(false);

  // Fetch place image ONCE on mount
  useEffect(() => {
    let mounted = true;
    
    const fetchPlaceImage = async () => {
      try {
        const result = await getPlacePhotos(location, { maxPhotos: 1 }); // Only get 1 photo
        
        if (!mounted) return;
        
        const validPhotos = result.photos.filter(url => 
          typeof url === 'string' && url.trim() !== ''
        );
        
        if (validPhotos.length > 0) {
          setLocationImage(validPhotos[0]); // Use first image only
        }
      } catch (error) {
        console.error('[LocationRecommendation] Error fetching place image:', error);
      }
    };

    fetchPlaceImage();
    
    return () => {
      mounted = false;
    };
  }, [location.id]); // Only depend on location.id

  const handleClick = () => {
    onClick?.();
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFavorite(!isFavorite);
  };

  // Get type info for the badge
  const typeInfo = getLocationTypeAndIcon(location, 12);
  
  // Format the display name
  const displayName = location.name.split(',')[0];
  
  // Use actual review count from location data
  const reviewCount = location.reviews || 0;
  
  // Get location info - use city, formattedAddress, or a default
  const locationInfo = location.city || 
                      location.formattedAddress?.split(',')[1]?.trim() || 
                      location.formattedAddress?.split(',')[0]?.trim() ||
                      location.country || 
                      'Location';
  
  // Debug log to check data
  if (!location.city && !location.formattedAddress) {
    console.log('[LocationRecommendation] Missing location data:', {
      name: location.name,
      city: location.city,
      formattedAddress: location.formattedAddress,
      country: location.country
    });
  }

  return (
    <div 
      className={`relative bg-white rounded-xl overflow-hidden cursor-pointer
                transition-all duration-500 ease-out transform
                ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
                ${isActive ? 'ring-2 ring-blue-500' : ''}`}
      style={{ 
        transitionDelay: `${index * 150}ms`,
        width: '100%',
        maxWidth: '170px',
        boxShadow: isActive 
          ? '0 8px 16px rgba(0,0,0,0.12)' 
          : '0 1px 2px rgba(0,0,0,0.05)',
        transform: isActive ? 'translateY(-2px)' : 'translateY(0)'
      }}
      onClick={handleClick}
      onMouseEnter={() => onHoverEnter?.(location.id)}
      onMouseLeave={() => onHoverLeave?.()}
    >
      {/* Image Section with Badge and Heart */}
      <div className="relative" style={{ paddingBottom: '100%' }}> {/* Portrait aspect ratio */}
        {/* Badge - Top Left */}
        <div className="absolute top-3 left-3 z-10">
          <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-white rounded-full shadow-md">
            {React.cloneElement(typeInfo.icon, { className: "w-3 h-3" })}
            <span className="text-xs font-medium text-gray-800">{typeInfo.label}</span>
          </div>
        </div>
        
        {/* Heart Icon - Top Right */}
        {/* <button 
          onClick={handleFavoriteClick}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/10 hover:bg-black/20 transition-colors"
        >
          <Heart 
            className={`w-5 h-5 transition-all ${
              isFavorite ? 'fill-red-500 text-red-500' : 'text-white'
            }`} 
          />
        </button> */}
        
        {/* Image Container */}
        <div className="absolute inset-0">
          {/* Loading placeholder */}
          <div 
            className={`absolute inset-0 bg-gray-200 flex items-center justify-center transition-opacity duration-300 ${
              imageLoaded ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
          >
            <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
          </div>
          
          {/* Single Image */}
          {locationImage ? (
            <img 
              src={locationImage} 
              alt={displayName} 
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/placeholder-image.png';
                setImageLoaded(true);
              }}
            />
          ) : (
            /* Fallback gradient when no image */
            <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300" />
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="p-3">
        {/* Title and Rating Row */}
        <div className="flex justify-between items-start mb-1.5">
          <h3 className="font-semibold text-gray-900 line-clamp-1 flex-.5 mr-1" 
              style={{ fontSize: '14px' }}>
            {displayName}
          </h3>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Star className="w-3 h-3 fill-current text-yellow-500 translate-y-[5px]" />
            <span className="text-xs font-medium leading-none translate-y-[6px]">{location.rating.toFixed(1)}</span>
          </div>
        </div>
        
        {/* Location info */}
        <p className="text-gray-500 text-xs line-clamp-1 mb-1"
          style={{ fontSize: '11.5px' }}>
          {locationInfo}
        </p>
        
        {/* Reviews count */}
       {/*  <p className="text-gray-400 text-xs">
          {reviewCount} reviews
        </p> */}
      </div>
    </div>
  );
};